import type { IconName } from '@palmetto/dsm-components';
import { PalmettoIcon } from '@palmetto/dsm-components';
import { Icon } from '@palmetto/palmetto-components';
import classNames from 'classnames';

import { Button, Card, useComponentTracking, useNavigation, useTranslation } from '@helios/frontend-core';

import { type FeedItemResponseDto } from '@helios/frontend-api-v2-client';
import {
  ApiCtaType,
  ApiCustomFeedItem,
  ApiFeedItemStatus,
  ApiFeedItemType,
  FeedItemDisplayParams,
  FeedItemReward,
} from '../../../../api/feed/types';
import { createContextUrl } from '../../../../utils/contextUrl';
import { FeedItemFunctionParams } from '../../feed-item-function-map';
import styles from './FeedItem.module.scss';

export interface FeedItemProps {
  status?: ApiFeedItemStatus | FeedItemResponseDto['status'];
  type?: ApiFeedItemType | FeedItemResponseDto['type'];
  ctaType?: ApiCtaType | FeedItemResponseDto['ctaType'];
  ctaUrl?: string;
  onCtaClick?: (params?: FeedItemFunctionParams) => boolean | void | Promise<boolean | void>;
  customItem?: ApiCustomFeedItem;
  displayParams?: FeedItemDisplayParams;
  imageUrl?: string;
  rewards?: FeedItemResponseDto['rewards'] | FeedItemReward[];
  feedItemId?: string;
  projectId?: string;
  onContactSupport?: () => void;
  iconClassName?: string;
  statusClassName?: string;
}

const iconMap: Partial<Record<ApiFeedItemType | ApiCustomFeedItem, IconName>> = {
  [ApiFeedItemType.achRequirement]: 'moneyBankAccount',
  [ApiFeedItemType.identityVerification]: 'identificationCard',
  [ApiFeedItemType.titleVerification]: 'homeDocument',
  [ApiFeedItemType.utilityBill]: 'documentBill',
  [ApiFeedItemType.utilityBillRequirement]: 'documentBill',
  [ApiCustomFeedItem.noActionableItems]: 'celebrate',
  [ApiFeedItemType.lightReachWelcomeQuestionnaire]: 'documentChecklist',
  [ApiFeedItemType.signContract]: 'writeSignature',
  [ApiFeedItemType.protectedClassConfirmation]: 'documentChecklist',
  [ApiFeedItemType.lightReachHVACSignOff]: 'clipboardCheck',
  [ApiFeedItemType.hvacLightreachWelcomeQuestionnaire]: 'documentChecklist',
  [ApiFeedItemType.lightReachEnergyBackupSignOff]: 'documentChecklist',
  [ApiFeedItemType.creditQualificationRequirement]: 'userCheck',
  [ApiFeedItemType.waterHeaterLightreachWelcomeQuestionnaire]: 'documentChecklist',
  [ApiFeedItemType.lightReachWaterHeaterSignOff]: 'clipboardCheck',
  [ApiFeedItemType.geothermalHeatPumpLightreachWelcomeQuestionnaire]: 'documentChecklist',
  [ApiFeedItemType.lightReachGeothermalHeatPumpSignOff]: 'clipboardCheck',
  [ApiFeedItemType.generatorLightreachWelcomeQuestionnaire]: 'documentChecklist',
  [ApiFeedItemType.lightReachGeneratorSignOff]: 'clipboardCheck',
  [ApiFeedItemType.kycMismatch]: 'searchMagnifyingGlassOff',
  [ApiFeedItemType.welcomeCall]: 'phone',
};

const FeedItem = (props: FeedItemProps): JSX.Element | null => {
  const {
    status,
    ctaUrl,
    type,
    ctaType,
    onCtaClick,
    customItem,
    displayParams,
    imageUrl,
    rewards,
    projectId,
    iconClassName,
    statusClassName,
  } = props;

  useComponentTracking(`feed-item-${type}`);

  const { t } = useTranslation();
  const { openURL } = useNavigation();

  const typeIconName = (type && iconMap[type as ApiFeedItemType]) || (customItem && iconMap[customItem]);
  const isSuccessful = status === ApiFeedItemStatus.completed || status === ApiFeedItemStatus.confirmed;
  const isWarning = status === ApiFeedItemStatus.rejected;

  const translationOptions = displayParams?.rejectionReason
    ? { ...displayParams, context: displayParams.rejectionReason as string }
    : displayParams;

  const statusKey = customItem ? `customItem.${customItem}.status` : `feedItem.status.${status}`;
  const statusText = t(statusKey, { defaultValue: '' });
  const shouldDisplayButton = ctaType && (ctaUrl || onCtaClick) && !isSuccessful;

  if (isSuccessful && status !== ApiFeedItemStatus.confirmed) {
    return null;
  }

  const ctaUrlWithContext =
    projectId && ctaUrl ? createContextUrl(ctaUrl, { id: projectId, type: 'project' }) : undefined;

  return (
    <Card className={styles.feedItem} semiTransparentBg>
      {statusText && (
        <div className={styles.labelContainer}>
          <span className={classNames(styles.status, 'heading-200', statusClassName ?? 'text-text-tertiary')}>
            {isSuccessful && <Icon name="check" className={styles.statusIcon} />}
            {isWarning && <Icon name="t-warning" className={classNames(styles.statusIcon, styles.warningIcon)} />}
            {statusText}
          </span>
        </div>
      )}

      {typeIconName && <PalmettoIcon name={typeIconName} className={classNames(styles.icon, iconClassName)} />}
      <div className={styles.text}>
        <h2>
          {t(
            `${customItem ? `customItem.${customItem}.heading` : `feedItem.type.${type}.${status}.heading`}`,
            translationOptions,
          )}
        </h2>
        <p
          className={classNames(styles.body, 'links-container')}
          dangerouslySetInnerHTML={{
            __html: t(
              `${customItem ? `customItem.${customItem}.body` : `feedItem.type.${type}.${status}.body`}`,
              translationOptions,
            ),
          }}
        />
      </div>
      <div className={styles.cta}>
        {imageUrl && (
          <img className={styles.image} src={imageUrl} alt={t(`feedItem.type.${type}.${status}.heading`)} />
        )}
        {shouldDisplayButton && (
          <Button
            trackingId={`feed-item-${type}-cta`}
            size="small"
            href={onCtaClick ? undefined : ctaUrlWithContext || ctaUrl}
            as={onCtaClick ? 'button' : ctaUrlWithContext || ctaUrl ? 'a' : 'button'}
            onClick={
              onCtaClick
                ? async () => {
                    const handled = await onCtaClick({ projectId, openURL });
                    if (!handled) {
                      const url = ctaUrlWithContext || ctaUrl;
                      if (url) openURL(url);
                    }
                  }
                : undefined
            }
          >
            {t(`feedItem.type.${type}.${status}.cta`, translationOptions)}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default FeedItem;
